import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import { useAlert } from './CustomAlert/AlertContext';
import api from '../utils/api';
import './RoomSelectionSchedule.css';

const TeamManagementView = ({ onBack, hideNavbar = false }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [selectedUser, setSelectedUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const { showConfirm, showAlert } = useAlert();

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone_number: '',
    user_type: 'HR'
  });

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const currentUserId = currentUser.id;

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/auth/users', {
        headers: { 'x-user-id': currentUserId }
      });
      setUsers(res.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      showAlert('Failed to fetch team users.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const filteredUsers = users.filter(user => 
    user.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const openAddModal = () => {
    setModalMode('add');
    setFormData({ full_name: '', email: '', phone_number: '', user_type: 'HR' });
    setShowModal(true);
  };

  const openEditModal = (user) => {
    setModalMode('edit');
    setSelectedUser(user);
    setFormData({
      full_name: user.full_name,
      email: user.email,
      phone_number: user.phone_number || '',
      user_type: user.user_type
    });
    setShowModal(true);
  };

  const handleDelete = async (user) => {
    if (user.id === currentUserId) {
      showAlert('You cannot remove yourself.', 'error');
      return;
    }
    const confirmed = await showConfirm(
      `Are you sure you want to remove ${user.full_name}? They will lose access to the platform.`
    );
    if (confirmed) {
      setLoading(true);
      try {
        await api.delete(`/api/auth/users/${user.id}`, {
          headers: { 'x-user-id': currentUserId }
        });
        showAlert('User removed successfully', 'success');
        fetchUsers();
      } catch (error) {
        showAlert(error.response?.data?.error || 'Failed to remove user', 'error');
        setLoading(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (modalMode === 'add') {
        await api.post('/api/auth/users', formData, {
          headers: { 'x-user-id': currentUserId }
        });
        showAlert('User added successfully.', 'success');
      } else {
        await api.put(`/api/auth/users/${selectedUser.id}`, formData, {
          headers: { 'x-user-id': currentUserId }
        });
        showAlert('User updated successfully', 'success');
      }
      setShowModal(false);
      fetchUsers();
    } catch (error) {
      showAlert(error.response?.data?.error || 'Failed to save user', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="room-selection-container fade-in">
      {!hideNavbar && (
        <Navbar 
          isLoggedIn 
          userName={currentUser.full_name} 
          onHome={onBack || (() => navigate('/interview'))} 
        />
      )}

      <div className="main-content" style={{ padding: '40px', maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ marginBottom: '20px' }}>
          <button 
            className="schedule-back-btn" 
            onClick={onBack || (() => navigate('/interview'))}
          >
            <span aria-hidden="true">←</span> Back to Dashboard
          </button>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
          <div>
            <h1 style={{ fontSize: '32px', fontWeight: '800', color: 'var(--text-primary)', marginBottom: '8px' }}>
              Team Management
            </h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: '16px' }}>
              Manage users and their roles for your organization.
            </p>
          </div>
          <button 
            onClick={openAddModal}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              borderRadius: '999px',
              border: 'none',
              background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
              color: '#ffffff',
              fontWeight: '700',
              cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(99, 102, 241, 0.3)',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 18px rgba(99, 102, 241, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'none';
              e.currentTarget.style.boxShadow = '0 4px 14px rgba(99, 102, 241, 0.3)';
            }}
          >
            <span style={{ fontSize: '20px', lineHeight: '1' }}>+</span> Add User
          </button>
        </div>

        <div style={{
          background: 'var(--bg-card)',
          borderRadius: '24px',
          padding: '24px',
          boxShadow: 'var(--shadow-md)',
          border: '1px solid var(--border-card)'
        }}>
          <div style={{ marginBottom: '24px' }}>
            <input 
              type="text" 
              placeholder="Search by name or email..." 
              value={searchQuery}
              onChange={handleSearch}
              style={{
                width: '100%',
                maxWidth: '400px',
                padding: '12px 20px',
                borderRadius: '12px',
                border: '1px solid var(--border-color)',
                background: 'var(--bg-surface)',
                color: 'var(--text-primary)',
                fontSize: '15px'
              }}
            />
          </div>

          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px', gap: '16px' }}>
              <div className="schedule-docx-spinner"></div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '15px', fontWeight: '500' }}>
                Loading team users...
              </div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                    <th style={{ padding: '16px', color: 'var(--text-secondary)', fontWeight: '600' }}>Name</th>
                    <th style={{ padding: '16px', color: 'var(--text-secondary)', fontWeight: '600' }}>Email</th>
                    <th style={{ padding: '16px', color: 'var(--text-secondary)', fontWeight: '600' }}>Role</th>
                    <th style={{ padding: '16px', color: 'var(--text-secondary)', fontWeight: '600' }}>Project</th>
                    <th style={{ padding: '16px', color: 'var(--text-secondary)', fontWeight: '600' }}>Exit Status</th>
                    <th style={{ padding: '16px', color: 'var(--text-secondary)', fontWeight: '600' }}>Joined</th>
                    <th style={{ padding: '16px', color: 'var(--text-secondary)', fontWeight: '600', textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
                        No team users found.
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map(user => (
                      <tr key={user.id} style={{ borderBottom: '1px solid var(--border-color)', transition: 'background 0.2s' }}>
                        <td style={{ padding: '16px', fontWeight: '500', color: 'var(--text-primary)' }}>{user.full_name}</td>
                        <td style={{ padding: '16px', color: 'var(--text-secondary)' }}>{user.email}</td>
                        <td style={{ padding: '16px' }}>
                          <span style={{
                            padding: '4px 12px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: '600',
                            background: user.user_type === 'HR' ? 'rgba(79, 70, 229, 0.1)' : 
                                        user.user_type === 'MANAGER' ? 'rgba(249, 115, 22, 0.1)' :
                                        user.user_type === 'BU_HEAD' ? 'rgba(147, 51, 234, 0.1)' :
                                        'rgba(13, 148, 136, 0.1)',
                            color: user.user_type === 'HR' ? '#4f46e5' : 
                                   user.user_type === 'MANAGER' ? '#ea580c' :
                                   user.user_type === 'BU_HEAD' ? '#9333ea' :
                                   '#0d9488'
                          }}>
                            {user.user_type}
                          </span>
                        </td>
                        <td style={{ padding: '16px', color: 'var(--text-secondary)' }}>
                          {user.project_name || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Not Assigned</span>}
                        </td>
                        <td style={{ padding: '16px' }}>
                          {user.exit_status === 'EXIT_PENDING' ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              <span style={{ padding: '3px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '700', backgroundColor: '#fef3c7', color: '#b45309', display: 'inline-block', width: 'fit-content' }}>
                                Exit Pending
                              </span>
                              {user.last_working_day && (
                                <span style={{ fontSize: '11px', color: '#b45309' }}>LWD: {new Date(user.last_working_day).toLocaleDateString()}</span>
                              )}
                            </div>
                          ) : user.exit_status === 'EXIT_APPROVED' ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              <span style={{ padding: '3px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '700', backgroundColor: '#dcfce7', color: '#15803d', display: 'inline-block', width: 'fit-content' }}>
                                Exit Approved
                              </span>
                              {user.last_working_day && (
                                <span style={{ fontSize: '11px', color: '#15803d' }}>LWD: {new Date(user.last_working_day).toLocaleDateString()}</span>
                              )}
                            </div>
                          ) : (
                            <span style={{ color: '#0d9488', fontWeight: '600', fontSize: '13px' }}>Active</span>
                          )}
                        </td>
                        <td style={{ padding: '16px', color: 'var(--text-secondary)' }}>
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                        <td style={{ padding: '16px', textAlign: 'right' }}>
                          <button 
                            onClick={() => openEditModal(user)}
                            style={{ background: 'transparent', border: 'none', color: '#4f46e5', cursor: 'pointer', marginRight: '16px', fontWeight: '600' }}
                          >
                            Edit
                          </button>
                          {user.id !== currentUserId && (
                            <button 
                              onClick={() => handleDelete(user)}
                              style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontWeight: '500' }}
                            >
                              Remove
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)} style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{
            background: 'var(--bg-card)', padding: '32px', borderRadius: '24px', width: '100%', maxWidth: '500px', boxShadow: 'var(--shadow-xl)'
          }}>
            <h2 style={{ fontSize: '24px', fontWeight: '800', marginBottom: '24px', color: 'var(--text-primary)' }}>
              {modalMode === 'add' ? 'Add User' : 'Edit User'}
            </h2>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-secondary)' }}>Full Name</label>
                <input 
                  type="text" 
                  value={formData.full_name}
                  onChange={e => setFormData({...formData, full_name: e.target.value})}
                  required
                  style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid var(--border-color)', background: 'var(--bg-surface)', color: 'var(--text-primary)' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-secondary)' }}>Email</label>
                <input 
                  type="email" 
                  value={formData.email}
                  onChange={e => setFormData({...formData, email: e.target.value})}
                  required
                  disabled={modalMode === 'edit'} // Don't allow changing email for now
                  style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid var(--border-color)', background: modalMode === 'edit' ? 'var(--bg-page)' : 'var(--bg-surface)', color: 'var(--text-primary)' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-secondary)' }}>Phone (Optional)</label>
                <input 
                  type="text" 
                  value={formData.phone_number}
                  onChange={e => setFormData({...formData, phone_number: e.target.value})}
                  style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid var(--border-color)', background: 'var(--bg-surface)', color: 'var(--text-primary)' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-secondary)' }}>Role</label>
                <select 
                  value={formData.user_type}
                  onChange={e => setFormData({...formData, user_type: e.target.value})}
                  style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid var(--border-color)', background: 'var(--bg-surface)', color: 'var(--text-primary)' }}
                >
                  <option value="HR">HR Admin</option>
                  <option value="MANAGER">Project Manager</option>
                  <option value="BU_HEAD">Business Unit Head</option>
                  <option value="INTERVIEWER">Technical Interviewer / Employee</option>
                </select>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}>
                <button type="button" disabled={submitting} onClick={() => setShowModal(false)} style={{ padding: '12px 24px', borderRadius: '30px', background: 'transparent', border: '1px solid var(--border-color)', color: 'var(--text-primary)', cursor: submitting ? 'not-allowed' : 'pointer', fontWeight: '600', opacity: submitting ? 0.6 : 1 }}>
                  Cancel
                </button>
                <button type="submit" disabled={submitting} style={{ padding: '12px 24px', borderRadius: '30px', background: 'linear-gradient(135deg, #6366f1, #4f46e5)', border: 'none', color: '#fff', cursor: submitting ? 'not-allowed' : 'pointer', fontWeight: '600', opacity: submitting ? 0.8 : 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {submitting && <div className="schedule-docx-spinner" style={{ width: '16px', height: '16px', borderWidth: '2px', borderTopColor: '#fff', animationDuration: '0.6s' }}></div>}
                  {modalMode === 'add' ? (submitting ? 'Adding...' : 'Add User') : (submitting ? 'Saving...' : 'Save Changes')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamManagementView;
